<template>
  <block-item-card
    :title="$t('Rates.Section.PeersNIISensitivity.Title')"
    :title-info="$t('Rates.Section.PeersNIISensitivity.TitleInfo')"
    :tooltip="$t('Rates.Section.PeersNIISensitivity.Tooltip')"
    class="fill-height"
  >
    <div class="pa-4">
      <!-- Header -->
      <div class="d-flex mb-3">
        <div class="flex-grow-1">
          <!-- Bank Name column - no header needed -->
        </div>
        <div
          class="text-center"
          style="width: 120px; position: relative; left: -8%;"
        >
          <span
            class="text-caption text-grey-600 d-block ml-6"
            style="line-height: 1.2;"
          >{{ assetLabel }}</span>
        </div>
        <div
          class="text-center"
          style="width: 120px; position: relative; left: -3%;"
        >
          <span
            class="text-caption text-grey-600 d-block ml-6"
            style="line-height: 1.2;"
          >Slope</span>
        </div>
      </div>

      <!-- Bank Cards -->
      <div class="bank-cards">
        <div
          v-for="(item, index) in data"
          :key="index"
          class="bank-card mb-3"
          :class="{'highlighted-bank': item.isBank}"
        >
          <div class="d-flex align-center">
            <!-- Bank Name -->
            <div
              class="flex-grow-1"
              style="min-width: 50%;"
            >
              <span
                class="bank-name"
                :class="{'highlighted-bank-name': item.isBank}"
              >{{ item.bankName }}</span>
            </div>

            <!-- SOFR -->
            <div
              class="d-flex align-center justify-center"
              style="width: 120px;"
            >
              <span
                class="sofr-value mr-2"
                :class="{'highlighted-value': item.isBank, 'neg': item.sofr < 0}"
              >{{ item.sofr }}%</span>
              <div class="custom-progress-circle">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                >
                  <!-- Background circle -->
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    fill="white"
                    stroke="#DFE3EB"
                    stroke-width="1"
                  />
                  <!-- Filled progress circle -->
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    fill="none"
                    stroke="none"
                    :stroke-dasharray="2 * Math.PI * 10"
                    :stroke-dashoffset="2 * Math.PI * 10 * (1 - item.sofr / 100)"
                    stroke-linecap="round"
                    transform="rotate(-90 12 12)"
                  />
                  <!-- Fill the center area based on percentage -->
                  <path
                    :d="getProgressPath(item.sofr)"
                    :fill="getSofrColor(item.sofr)"
                  />
                  <!-- Border circle on top -->
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    fill="none"
                    stroke="#DFE3EB"
                    stroke-width="1"
                  />
                </svg>
              </div>
            </div>

            <!-- Slope -->
            <div
              class="d-flex align-center justify-center"
              style="width: 120px; margin-left: 30px;"
            >
              <span
                class="slope-value mr-2"
                :class="{'highlighted-value': item.isBank, 'neg': item.slope < 0}"
              >{{ item.slope }}%</span>
              <div class="custom-progress-circle">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                >
                  <!-- Background circle -->
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    fill="white"
                    stroke="#DFE3EB"
                    stroke-width="1"
                  />
                  <!-- Filled progress circle -->
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    fill="none"
                    stroke="none"
                    :stroke-dasharray="2 * Math.PI * 10"
                    :stroke-dashoffset="2 * Math.PI * 10 * (1 - item.slope / 100)"
                    stroke-linecap="round"
                    transform="rotate(-90 12 12)"
                  />
                  <!-- Fill the center area based on percentage -->
                  <path
                    :d="getProgressPath(item.slope)"
                    :fill="getSlopeColor(item.slope)"
                  />
                  <!-- Border circle on top -->
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    fill="none"
                    stroke="#DFE3EB"
                    stroke-width="1"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </block-item-card>
</template>

<script>
import BlockItemCard from '@/theme/default/components/common/BlockItemCard'
import chartsColorMixin from '@/theme/default/components/charts/mixins/chartsColorMixin'
export default {
  name      : 'PeersNIISensitivity',
  components: { BlockItemCard },
  mixins    : [chartsColorMixin],
  props     : {
    data: {
      type   : Array,
      default: () => []
    },
    highlightedBank: {
      type   : String,
      default: 'CTBC Bank'
    },
    assetLabel: {
      type   : String,
      default: ''
    }
  },
  methods: {
    getSofrColor (value) {
      return value < 0 ? this.themeColorRGBA('synthesis-ui-red-02') : this.themeColorRGBA('synthesis-ui-blue-04') // Light red for all SOFR values
    },
    getSlopeColor (value) {
      return value < 0 ? this.themeColorRGBA('synthesis-ui-red-02') : this.themeColorRGBA('synthesis-ui-yellow-01') // Orange for all Slope values
    },
    getProgressPath (percentage) {
      const radius = 10
      const centerX = 12
      const centerY = 12

      if (percentage >= 100) {
        // Full circle
        return `M ${centerX},${centerY} m -${radius},0 a ${radius},${radius} 0 1,0 ${2 * radius},0 a ${radius},${radius} 0 1,0 -${2 * radius},0`
      }

      // Calculate the angle for the percentage
      const angle = (Math.abs(percentage) / 100) * 2 * Math.PI
      const endAngle = angle // Start from right (3 o'clock) instead of top (12 o'clock)

      // Calculate end point
      const endX = centerX + radius * Math.cos(endAngle)
      const endY = centerY + radius * Math.sin(endAngle)

      // Determine if we need to draw the large arc
      const largeArcFlag = Math.abs(percentage) > 50 ? 1 : 0

      return `M ${centerX + radius},${centerY} A ${radius},${radius} 0 ${largeArcFlag},1 ${endX},${endY} L ${centerX},${centerY} Z`
    }
  }
}
</script>

<style scoped>
.bank-cards {
  max-height: 362px;
  overflow-y: auto;
}

.bank-card {
  background: var(--v-synthesis-ui-grey-06-base);
  border-radius: 8px;
  padding: 8px 8px 8px 8px;
  transition: all 0.2s ease;
}

.bank-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.highlighted-bank {
  background-color: var(--v-synthesis-ui-green-08-base) !important;
  font-weight: 700;
}

.bank-name {
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  color: #333;
}

.highlighted-bank-name {
  font-weight: 700;
  font-style: normal;
}

.sofr-value {
  font-size: 14px;
  font-weight: 500;
  font-style: normal;
  color: #333;
  min-width: 60px;
  text-align: right;
}

.neg { color: var(--v-synthesis-ui-red-02-base) !important; }

.slope-value {
  font-size: 14px;
  font-weight: 500;
  font-style: normal;
  color: #333;
  min-width: 60px;
  text-align: right;
}

.highlighted-value {
  font-weight: 700;
  font-style: normal;
}

.custom-progress-circle {
  display: inline-block;
}

.custom-progress-circle svg {
  display: block;
}

/* Custom scrollbar for the cards container */
.bank-cards::-webkit-scrollbar {
  width: 6px;
}

.bank-cards::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.bank-cards::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.bank-cards::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.custom-block-item-card {
  min-height: 362px;
  max-height: 362px;
}
</style>
